import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'

import { RichBlock, parseRichBlocks } from '../../data/utils/rich-text.util'

/**
 * Pinta un texto del Tablero (descripción, criterios, pasos, comentario) con su formato y
 * resolviendo las imágenes que se pegaron dentro de él.
 *
 * ── La postura de seguridad cambió, y este es el registro de por qué y con qué ──
 *
 * Este componente decía: «no hay librería de markdown ni `innerHTML`… traer `marked` + un
 * sanitizador habría metido una superficie de HTML almacenado en un campo que llenan usuarios:
 * cualquier fallo del sanitizador se vuelve XSS con persistencia». Y cerraba: «el costo declarado
 * es que negritas, listas y ligas NO se renderizan… si algún día se piden, esa sí es la
 * conversación de traer un renderer de verdad».
 *
 * Se pidieron (F-041): buena parte de los intakes entra por el servidor MCP y venía con los
 * asteriscos y los backticks a la vista, ilegible.
 *
 * **La conversación se resolvió sin traer el renderer.** `parseRichBlocks` interpreta el marcado y
 * devuelve DATOS —bloques y marcas—, y esta plantilla los pinta con elementos reales mientras el
 * texto sigue saliendo por interpolación. Nunca se construye una cadena de HTML, así que no hay
 * nada que sanear.
 *
 * La defensa que sustituye a la anterior es **más fuerte, no más débil**: antes el argumento era
 * «no interpretamos nada, así que no hay riesgo»; ahora es «interpretamos, pero el resultado no
 * puede ser HTML porque no existe el camino que lo produciría». Con `marked` + `dompurify` la
 * seguridad habría dependido de que el sanitizador no fallara nunca — y el panel no tiene CSP en
 * ninguna capa, así que ese sanitizador habría sido la única defensa.
 *
 * Lo que sigue igual: la imagen va por `[src]` y la liga por `[href]`, los dos contextos que
 * Angular sanea por su cuenta (un `javascript:` no dispara nada).
 *
 * El costo nuevo, y es real: el lector interpreta más de lo que el editor sabe escribir. Está
 * explicado en el docblock de `rich-text.util.ts`.
 */
@Component({
  selector: 'app-worklog-rich-text',
  templateUrl: './worklog-rich-text.component.html',
  styleUrls: ['./worklog-rich-text.component.scss'],
})
export class WorklogRichTextComponent implements OnChanges {
  @Input() text: string | null | undefined = ''
  /**
   * URL del blob → object URL ya descargado por la API. El contenedor del bucket no es público, así
   * que la URL que vive en el texto NO sirve como `src`: hay que traducirla.
   */
  @Input() resolved: Record<string, string> = {}
  /**
   * URLs que SÍ son adjuntos de esta tarea. Es lo que separa «todavía no baja» de «no existe»: una
   * URL pegada como texto desde otra tarea nunca va a resolver, y antes de tener esta lista las dos
   * situaciones se veían igual (es decir, no se veían: el texto desaparecía sin aviso).
   */
  @Input() knownUrls: string[] = []
  /** Click en una imagen: el contenedor decide si la abre en grande. */
  @Output() openImage = new EventEmitter<string>()

  blocks: RichBlock[] = []
  private known = new Set<string>()

  ngOnChanges(): void {
    this.blocks = parseRichBlocks(this.text ?? '')
    this.known = new Set(this.knownUrls)
  }

  /** `src` utilizable para una imagen, o `null` si aún no se ha descargado. */
  src(url: string): string | null {
    return this.resolved[url] ?? null
  }

  /**
   * En qué estado está la imagen. Se distingue «cargando» de «no disponible» para no acusar de rota
   * una imagen que solo va en camino: mientras no se sepa qué adjuntos tiene la tarea
   * (`knownUrls` vacío) se asume lo benigno.
   */
  state(url: string): 'ready' | 'loading' | 'missing' {
    if (this.resolved[url]) return 'ready'
    if (!this.known.size || this.known.has(url)) return 'loading'
    return 'missing'
  }
}
